export type ComplianceSettingScope = 'TENANT' | 'TENANT_AND_FACILITY';
export type LegalBasis = 'NONE_TARGET' | 'UNVERIFIED';
import { DEFAULT_COMPLAINT_CATEGORY_SET, type ComplaintCategorySet } from '../complaint-categories';
export type { ComplaintCategory, ComplaintCategorySet } from '../complaint-categories';
export interface NumberSettingDefinition {
  key: string;
  tier: 'T2' | 'T3';
  kind: 'NUMBER_WITH_UNIT';
  unit: 'hours' | 'days' | 'business_days';
  platformDefault: number;
  minimum: number;
  maximum: number;
  tighterDirection?: 'LOWER_IS_TIGHTER' | 'HIGHER_IS_TIGHTER';
  scope: ComplianceSettingScope;
  label: string;
  description: string;
  legalBasis: LegalBasis;
  sourceConstant: string;
}
export interface CategorySettingDefinition {
  key: 'complaint_categories';
  tier: 'T3';
  kind: 'OBJECT';
  platformDefault: ComplaintCategorySet;
  scope: 'TENANT_AND_FACILITY';
  label: string;
  description: string;
  legalBasis: 'NONE_TARGET';
  sourceConstant: string;
}
export type ComplianceSettingDefinition = NumberSettingDefinition | CategorySettingDefinition;
export const COMPLIANCE_T1_KEYS = [
  'sirs_priority_1_report_hours',
  'sirs_priority_2_report_days',
  'sirs_final_report_days',
  'ndb_assessment_days',
  'rn_24_7_monthly_report_day',
] as const;
/** Only these provider-owned review defaults may be lengthened with a reason. */
export const REASONED_CADENCE_KEYS = [
  'risk_review_low_cadence_days',
  'risk_review_medium_cadence_days',
  'risk_review_high_cadence_days',
  'risk_review_extreme_cadence_days',
  'policy_review_cadence_days',
  'association_review_cadence_days',
] as const;

export function isReasonedCadenceKey(key: string): boolean {
  return (REASONED_CADENCE_KEYS as readonly string[]).includes(key);
}
const categories: ComplaintCategorySet = DEFAULT_COMPLAINT_CATEGORY_SET;
const n = (
  key: string,
  tier: NumberSettingDefinition['tier'],
  unit: NumberSettingDefinition['unit'],
  platformDefault: number,
  minimum: number,
  maximum: number,
  sourceConstant: string,
  legalBasis: LegalBasis = 'NONE_TARGET',
): NumberSettingDefinition => ({
  key,
  tier,
  kind: 'NUMBER_WITH_UNIT',
  unit,
  platformDefault,
  minimum,
  maximum,
  ...(tier === 'T2' ? { tighterDirection: 'LOWER_IS_TIGHTER' as const } : {}),
  scope: 'TENANT_AND_FACILITY',
  label: `${key.charAt(0).toUpperCase()}${key.slice(1).replaceAll('_', ' ')}`,
  description: 'Platform operational target.',
  legalBasis,
  sourceConstant,
});
/** Current literals are retained only as a foundation; no consumer switches in this slice. */
export const COMPLIANCE_SETTINGS_REGISTRY = [
  n(
    'complaint_acknowledgement_target',
    'T2',
    'business_days',
    3,
    1,
    3,
    'supabase/live-schema-2026-09-16.sql:393064',
  ),
  n(
    'complaint_ageing_target',
    'T2',
    'days',
    30,
    1,
    30,
    'packages/utils/src/compliance-deadlines/complaints.ts:9',
  ),
  n(
    'open_disclosure_target',
    'T2',
    'hours',
    24,
    1,
    24,
    'packages/utils/src/compliance-deadlines/open-disclosure.ts:45',
  ),
  // These targets schedule the next provider-owned review. They do not alter
  // a statutory clock or an individual plan's explicitly recorded due date.
  n(
    'risk_review_low_cadence_days',
    'T2',
    'days',
    180,
    1,
    730,
    'supabase/live-schema-2026-09-16.sql:417257',
  ),
  n(
    'risk_review_medium_cadence_days',
    'T2',
    'days',
    180,
    1,
    730,
    'supabase/live-schema-2026-09-16.sql:417257',
  ),
  n(
    'risk_review_high_cadence_days',
    'T2',
    'days',
    180,
    1,
    730,
    'supabase/live-schema-2026-09-16.sql:417257',
  ),
  n(
    'risk_review_extreme_cadence_days',
    'T2',
    'days',
    180,
    1,
    730,
    'supabase/live-schema-2026-09-16.sql:417257',
  ),
  n(
    'policy_review_cadence_days',
    'T2',
    'days',
    365,
    1,
    730,
    'supabase/migrations/20269730100000_document_expiry_policy_lifecycle_s11.sql:684',
  ),
  n(
    'association_review_cadence_days',
    'T2',
    'days',
    365,
    1,
    730,
    'supabase/migrations/20274000000013_compliance_association_controls.sql:296',
  ),
  n(
    'restrictive_practice_chemical_review_cadence_days',
    'T2',
    'days',
    30,
    1,
    30,
    'supabase/live-schema-2026-09-16.sql:80020',
    'UNVERIFIED',
  ),
  n(
    'restrictive_practice_other_review_cadence_days',
    'T2',
    'days',
    180,
    1,
    180,
    'supabase/live-schema-2026-09-16.sql:80021',
    'UNVERIFIED',
  ),
  n(
    'restrictive_practice_review_warning_lead',
    'T3',
    'days',
    14,
    1,
    90,
    'packages/utils/src/compliance-deadlines/restrictive-practice.ts:38',
    'UNVERIFIED',
  ),
  n(
    'restrictive_practice_emergency_review_reminder_lead',
    'T3',
    'hours',
    4,
    1,
    168,
    'packages/utils/src/compliance-deadlines/restrictive-practice.ts:78',
    'UNVERIFIED',
  ),
  n(
    'risk_review_warning_lead',
    'T3',
    'days',
    14,
    1,
    90,
    'packages/utils/src/compliance-deadlines/governance.ts:90',
  ),
  n(
    'conflict_of_interest_warning_lead',
    'T3',
    'days',
    30,
    1,
    90,
    'packages/utils/src/compliance-deadlines/governance.ts:26',
  ),
  n(
    'governing_body_skills_warning_lead',
    'T3',
    'days',
    30,
    1,
    90,
    'packages/utils/src/compliance-deadlines/governance.ts:58',
  ),
  n(
    'audit_schedule_warning_lead',
    'T3',
    'days',
    7,
    1,
    90,
    'packages/utils/src/compliance-deadlines/audit-program.ts:22',
  ),
  n(
    'ndb_assessment_reminder_lead',
    'T3',
    'days',
    7,
    1,
    90,
    'packages/utils/src/compliance-deadlines/data-breach.ts:26',
  ),
  n(
    'sirs_priority_1_t12_reminder_lead',
    'T3',
    'hours',
    12,
    1,
    168,
    'packages/utils/src/compliance-deadlines/sirs.ts:43',
  ),
  n(
    'sirs_priority_1_t4_reminder_lead',
    'T3',
    'hours',
    4,
    1,
    168,
    'packages/utils/src/compliance-deadlines/sirs.ts:43',
  ),
  n(
    'sirs_priority_1_t2_reminder_lead',
    'T3',
    'hours',
    2,
    1,
    168,
    'packages/utils/src/compliance-deadlines/sirs.ts:43',
  ),
  {
    key: 'complaint_categories',
    tier: 'T3',
    kind: 'OBJECT',
    platformDefault: categories,
    scope: 'TENANT_AND_FACILITY',
    label: 'Complaint categories',
    description: 'Labels may change, codes remain stable and OTHER cannot be removed.',
    legalBasis: 'NONE_TARGET',
    sourceConstant: 'apps/web/src/app/(dashboard)/compliance/complaints/new/page.tsx:57-94',
  },
] as const satisfies readonly ComplianceSettingDefinition[];
export type ComplianceSettingKey = (typeof COMPLIANCE_SETTINGS_REGISTRY)[number]['key'];
export function getComplianceSettingDefinition(
  key: string,
): ComplianceSettingDefinition | undefined {
  return COMPLIANCE_SETTINGS_REGISTRY.find((setting) => setting.key === key);
}
